class TimelineTabsElement extends HTMLElement {
  disconnectedCallback() {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }
  connectedCallback() {
    this.initialize();
  }

  initialize() {
    this.tabs = this.querySelectorAll('.timeline__content-item[role="tab"]');
    this.panels = this.querySelectorAll('.timeline__media-item[role="tabpanel"]');
    this.content = this.querySelector('.timeline__content');
    this._isComputing = false;
    this._pendingCompute = false;

    if (!this.tabs.length || !this.panels.length) return;

    // Compute CSS variables for sizing
    this.computeDimensions();

    // Recompute after fonts load (text metrics can change)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.computeDimensions());
    }

    // Bind events
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.setActiveTab(index, { focus: false }));
      tab.addEventListener('keydown', (e) => this.onTabKeydown(e, index));
    });

    // Recompute on resize
    this.onResize = this.debounce(() => this.computeDimensions(), 150);
    window.addEventListener('resize', this.onResize, { passive: true });

    // Observe element size changes (more reliable than window resize)
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this._isComputing) {
          this._pendingCompute = true;
          return;
        }
        this.computeDimensions();
      });
      this.resizeObserver.observe(this);
      if (this.content) this.resizeObserver.observe(this.content);
      // Do NOT observe each tab to avoid loops when setting CSS vars
    }

    // Ensure a single selected tab on load
    const initiallySelectedIndex = Array.from(this.tabs).findIndex((t) => t.getAttribute('aria-selected') === 'true');
    const startIndex = initiallySelectedIndex >= 0 ? initiallySelectedIndex : 0;
    this.setActiveTab(startIndex, { focus: false });

    // Final pass after full page load to capture accurate layout
    window.addEventListener('load', () => {
      // use two rAFs to ensure layout + styles are settled
      requestAnimationFrame(() => requestAnimationFrame(() => this.computeDimensions()));
    }, { once: true });
  }

  onTabKeydown(event, index) {
    const { key } = event;
    const lastIndex = this.tabs.length - 1;

    // Roving tabindex navigation
    if (key === 'ArrowRight' || key === 'ArrowDown') {
      event.preventDefault();
      const next = index === lastIndex ? 0 : index + 1;
      this.focusOnly(next);
      return;
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      event.preventDefault();
      const prev = index === 0 ? lastIndex : index - 1;
      this.focusOnly(prev);
      return;
    }

    if (key === 'Home') {
      event.preventDefault();
      this.focusOnly(0);
      return;
    }

    if (key === 'End') {
      event.preventDefault();
      this.focusOnly(lastIndex);
      return;
    }

    // Activate with Enter/Space
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.setActiveTab(index, { focus: true });
    }
  }

  focusOnly(index) {
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    this.tabs[index].focus({ preventScroll: true });
  }

  setActiveTab(index, { focus } = { focus: false }) {
    if (!this.tabs || !this.panels) return;
    const isSmallViewport = window.matchMedia('(max-width: 991px)').matches;
    if (isSmallViewport) {
      this.lockWidths();
    }

    this.tabs.forEach((tab, i) => {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      // hide all
      panel.hidden = true;
    });

    const activeTab = this.tabs[index];
    const controlsId = activeTab.getAttribute('aria-controls');
    if (controlsId) {
      const activePanel = this.querySelector(`#${CSS.escape(controlsId)}`);
      if (activePanel) {
        activePanel.hidden = false;
      }
    }

    if (focus) {
      this.tabs[index].focus();
    }

    // Recompute sizes since selected state changes layout
    this.computeDimensions();

    if (isSmallViewport) {
      // Allow layout and measurements to settle, then unlock
      requestAnimationFrame(() => requestAnimationFrame(() => this.unlockWidths()));
    }
  }

  computeDimensions() {
    if (!this.tabs) return;
    if (this._isComputing) {
      this._pendingCompute = true;
      return;
    }
    this._isComputing = true;
    // Phase 1: clear width constraints so flex can naturally size items
    this.tabs.forEach((tab) => tab.style.setProperty('--min-width', '0px'));
    // Phase 2: measure after layout
    requestAnimationFrame(() => {
      this.tabs.forEach((tab) => this.computeTabVars(tab));
      this.updateBaseHeight();
      this._isComputing = false;
      if (this._pendingCompute) {
        this._pendingCompute = false;
        this.computeDimensions();
      }
    });
  }

  computeTabVars(tabEl) {
    const wrapper = tabEl.querySelector('.timeline__content-wrapper');
    const title = tabEl.querySelector('.timeline__content-title');
    const text = tabEl.querySelector('.timeline__content-text');
    if (!wrapper || !title || !text) return;

    // Save inline styles to restore after measurement
    const prevTitleHeight = title.style.height;
    const prevTextVisibility = text.style.visibility;

    // Ensure natural sizes for measurement
    title.style.height = 'auto';
    text.style.visibility = 'hidden';
    // On small viewports, flex-grow can inflate height; neutralize it while measuring
    const isSmallViewport = window.matchMedia('(max-width: 991px)').matches;
    const prevFlex = tabEl.style.flex;
    if (isSmallViewport) {
      tabEl.style.flex = '0 0 auto';
    }

    const titleHeight = Math.ceil(title.scrollHeight);
    const textHeight = Math.ceil(text.scrollHeight);

    // Tailwind paddings when selected (p-6 => 24px, pb-10 => 40px)
    const tabPaddingY = 24 + 24;
    const wrapperPaddingBottom = 40;
    const tabPaddingX = 24 + 24;

    const contentHeight = titleHeight + textHeight;
    const maxHeight = contentHeight + tabPaddingY + wrapperPaddingBottom; // expanded
    const minHeight = Math.max(230, titleHeight + tabPaddingY);

    // Use the current flex-grown rendered width, clamp to 384px max
    const renderedWidth = Math.round(tabEl.getBoundingClientRect().width);
    const clampedWidth = Math.min(384, Math.max(0, renderedWidth));
    const minWidth = clampedWidth;

    if (isSmallViewport) {
      tabEl.style.setProperty('--max-height', `${maxHeight}px`);
      tabEl.style.setProperty('--min-height', `${minHeight}px`);
    } else {
      // Clear to allow desktop CSS to control heights
      tabEl.style.removeProperty('--max-height');
      tabEl.style.removeProperty('--min-height');
    }
    tabEl.style.setProperty('--min-width', `${minWidth}px`);

    // Restore inline styles
    title.style.height = prevTitleHeight;
    text.style.visibility = prevTextVisibility;
    if (isSmallViewport) {
      tabEl.style.flex = prevFlex;
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  updateBaseHeight() {
    // Measure the current rendered height of the component and expose as a CSS var
    const rect = this.getBoundingClientRect();
    const height = Math.max(0, Math.round(rect.height));
    this.style.setProperty('--base-height', `${height}px`);
  }

  lockWidths() {
    if (this._locked) return;
    this._locked = true;
    this._prevInlineFlex = [];
    this._prevInlineWidth = [];
    this.tabs.forEach((tab) => {
      this._prevInlineFlex.push(tab.style.flex);
      this._prevInlineWidth.push(tab.style.width);
      const rect = tab.getBoundingClientRect();
      tab.style.flex = '0 0 auto';
      tab.style.width = `${Math.round(rect.width)}px`;
    });
  }

  unlockWidths() {
    if (!this._locked) return;
    this.tabs.forEach((tab, i) => {
      tab.style.width = this._prevInlineWidth ? this._prevInlineWidth[i] : '';
      tab.style.flex = this._prevInlineFlex ? this._prevInlineFlex[i] : '';
    });
    this._locked = false;
  }
}

if (!customElements.get('timeline-tabs')) {
  customElements.define('timeline-tabs', TimelineTabsElement);
}


